import {
  ResponseResult,
  ResponseStatus,
  type QuestionResponseDetails,
  type SubQuestionResponseEntry,
  type UpdateAuditQuestionResponseCommandInput,
} from '@amzn/gbsa-helios-typescript-client';
import {
  buildPreservedResponseData,
  projectSubResponsesForPreservation,
} from './preserveResponseFields';

export interface BuildMediaUpdateArgs {
  readonly businessUnitId: string;
  readonly auditId: string;
  readonly questionId: string;
  readonly alias: string;
  /** Absent = parent slot; present = the target sub slot. */
  readonly subQuestionId?: string | undefined;
  /** Complete effective list for the slot (persisted ∪ new, pre-merged). */
  readonly mediaIds: readonly string[];
  /** Cached parent response — preservation source for the full PATCH. */
  readonly previousResponse: QuestionResponseDetails | undefined;
  /** Cached sub responses — preserved; only the target sub's ids change. */
  readonly subQuestionResponses: readonly SubQuestionResponseEntry[] | undefined;
}

/**
 * Pure mutation-input builder for an attachment save. The endpoint is
 * a full PATCH, so the whole previous response is re-asserted via the
 * shared preservation helpers and only the target slot's mediaIds
 * change. Status derives from the previous result (never hardcoded
 * COMPLETED — attach-before-answer stays PENDING); a sub slot with no
 * existing row gets a synthetic entry. Regression tests pin the
 * pre-CR-V10 wipe bug (buildMediaUpdate.test).
 */
export function buildMediaUpdate(
  args: BuildMediaUpdateArgs,
): UpdateAuditQuestionResponseCommandInput {
  const {
    businessUnitId,
    auditId,
    questionId,
    alias,
    subQuestionId,
    mediaIds,
    previousResponse,
    subQuestionResponses,
  } = args;

  // Read/write status enums differ — derive, don't pass through
  // (same rule as buildCommentUpdate).
  const responseResult = previousResponse?.responseResult;
  const responseStatus =
    responseResult === ResponseResult.NA
      ? ResponseStatus.NA
      : responseResult !== undefined
        ? ResponseStatus.COMPLETED
        : ResponseStatus.PENDING;

  const input: UpdateAuditQuestionResponseCommandInput = {
    businessUnitId,
    auditId,
    questionId,
    responseStatus,
    respondedBy: alias,
    lastUpdatedBy: alias,
  };

  if (responseResult !== undefined) {
    input.responseResult = responseResult;
  }

  const isSubSlot = subQuestionId !== undefined;

  // Parent block: replace mediaIds only when the parent slot is
  // the target; otherwise pure preservation.
  const parentBlock = buildPreservedResponseData(
    previousResponse,
    isSubSlot ? {} : { mediaIdsOverride: mediaIds },
  );
  if (parentBlock) {
    input.response = parentBlock;
  }

  // Sub-responses: project every existing entry with preservation;
  // replace only the target sub's mediaIds.
  const projected = projectSubResponsesForPreservation(
    subQuestionResponses,
    isSubSlot ? { mediaIdsOverrides: { [subQuestionId]: mediaIds } } : {},
  );

  // Attach-before-answer on a sub with no existing response row:
  // synthesize the entry so the upload isn't silently dropped.
  const hasExistingTarget =
    isSubSlot &&
    (subQuestionResponses ?? []).some((entry) => entry.subQuestionId === subQuestionId);
  if (isSubSlot && !hasExistingTarget && mediaIds.length > 0) {
    projected.push({ subQuestionId, mediaIds: [...mediaIds] });
  }

  if (projected.length > 0) {
    input.subResponses = projected;
  }

  return input;
}
